import Toolbar from '@mui/material/Toolbar';
import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Paper, Avatar, ToggleButton, ToggleButtonGroup, CircularProgress, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import Drawer from '../../components/Lead/Details/Drawer'
import BasicDetails from '../../components/Lead/Details/BasicDetails/BasicDetails';
import NextAction from '../../components/Lead/Details/NextAction/NextAction';
import FullDetails from '../../components/Lead/Details/FullDetails/FullDetails';
import Notes from '../../components/Common/Notes/Notes';
import Attachments from '../../components/Lead/Details/Attachments/Attachments';
import OpenActivities from '../../components/Lead/Details/OpenActivities/OpenActivities';
import useApiManager from '../../utils/Api/apiManager';
import { useNavigate } from "react-router-dom";
import ClosedActivities from '../../components/Lead/Details/ClosedActivities/ClosedActivities';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import Campaigns from '../../components/Lead/Details/Campaigns/Campaigns';
import EntityTimeline from '../../components/Common/Timeline/Timeline';
import { toast } from 'sonner';
import ProfileAvatar from '../../components/Common/ProfileAvatar/ProfileAvatar';

const LeadDetails = () => {
    const { leadId } = useParams(); // Get leadID from URL
    const [leadDetails, setLeadDetails] = useState(null);
    const [leadRelatedEntitiesCount, setLeadRelatedEntitiesCount] = useState(null);
    const [latestUpdate, setLatestUpdate] = useState('');
    const [leadNotes, setLeadNotes] = useState([]);
    const [leadAttachments, setLeadAttachments] = useState([]);
    const [leadTasks, setLeadTasks] = useState([]);
    const [leadClosedTasks, setClosedTasks] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(false);
    const { get, put } = useApiManager();
    const notesRef = useRef(null);
    const attachmentsRef = useRef(null);
    const openActivitiesRef = useRef(null);
    const closedActivitiesRef = useRef(null);
    const campaignsRef = useRef(null);
    const navigate = useNavigate();
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedView, setSelectedView] = useState("overview");

    const handleToggle = (event, newView) => {
        if (newView) {
            setSelectedView(newView);
        }
    };

    const handleOpenDialog = () => {
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleConfirmAction = () => {
        handleConvert();
        handleCloseDialog();
    };

    const scrollToSection = (ref) => {
        ref.current.scrollIntoView({ behavior: 'smooth' });
    };


    const fetchLeadDetails = async () => {
        setLoading(true);

        try {
            const data = await get(`/leads/${leadId}`);
            setLeadDetails(data);
            const firstName = data.firstName ?? '';
            const lastName = data.lastName ?? '';
            document.title = `${firstName} ${lastName} (Lead) - iLead CRM`.trim() || 'Lead Details';

        } catch (error) {
            console.error('Error fetching lead details:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchLeadRelatedEntitiesCount = async () => {
        setLoading(true);

        try {
            const data = await get(`/leads/${leadId}/related-entities-count`);
            setLeadRelatedEntitiesCount(data);
        } catch (error) {
            console.error('Error fetching lead entities count:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchLatestUpdate = async () => {
        setLoading(true);

        try {
            const data = await get(`/leads/${leadId}/latest-update`);
            setLatestUpdate(data);
        } catch (error) {
            console.error('Error fetching latest update:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchLeadNotes = async () => {
        setLoading(true);

        try {
            const notes = await get(`/leads/${leadId}/notes`);
            setLeadNotes(notes);
        } catch (error) {
            console.error('Error fetching lead notes:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchLeadAttachments = async () => {
        setLoading(true);

        try {
            const attachments = await get(`/leads/${leadId}/attachments`);
            setLeadAttachments(attachments);
        } catch (error) {
            console.error('Error fetching lead attachments:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchLeadTasks = async () => {
        setLoading(true);
        try {
            const tasks = await get(`/leads/${leadId}/tasks/opened`);
            setLeadTasks(tasks);
        } catch (error) {
            console.error('Error fetching lead tasks:', error);
        } finally {
            setLoading(false);
        }
    };
    const fetchLeadClosedTasks = async () => {
        setLoading(true);
        try {
            const tasks = await get(`/leads/${leadId}/tasks/closed`);
            setClosedTasks(tasks);
        } catch (error) {
            console.error('Error fetching lead closed tasks:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCampaigns = async () => {
        setLoading(true);
        try {
            const campaigns = await get(`/contacts/${leadId}/campaigns`);
            setCampaigns(campaigns);
        } catch (error) {
            console.error('Error fetching lead campaigns:', error);
        } finally {
            setLoading(false);
        }
    };

    const refreshLeadNotes = async () => {
        await fetchLeadNotes();
        await fetchLeadRelatedEntitiesCount();
        await fetchLatestUpdate();
    };

    const refreshLeadAttachments = async () => {
        await fetchLeadAttachments();
        await fetchLeadRelatedEntitiesCount();
        await fetchLatestUpdate();
    };

    const refreshLeadTasks = async () => {
        await fetchLeadTasks();
        await fetchLeadRelatedEntitiesCount();
        await fetchLatestUpdate();
    };

    const refreshLeadClosedTasks = async () => {
        await fetchLeadClosedTasks();
        await fetchLeadRelatedEntitiesCount();
        await fetchLatestUpdate();
    };

    const refreshCampaigns = async () => {
        await fetchCampaigns();
        await fetchLeadRelatedEntitiesCount();
        await fetchLatestUpdate();
    };

    useEffect(() => {
        fetchLeadDetails();
        fetchLeadRelatedEntitiesCount();
        fetchLatestUpdate();
        fetchLeadNotes();
        fetchLeadAttachments();
        fetchLeadTasks();
        fetchLeadClosedTasks();
        fetchCampaigns();
    }, []);

    const handleConvert = async () => {
        //console.log('Form Submitted:', formData);
        // Handle form submission logic here (e.g., send data to an API)
        try {
            const responseData = await put(`/leads/${leadId}/convert`);
            toast.success(`Lead: ${leadDetails.firstName} ${leadDetails.lastName} converted to a contact!`);
            navigate(`/leads/`);
            // Handle success (e.g., show a notification, redirect, etc.)
        } catch (error) {
            console.error('Failed to submit form:', error);
            // Handle error (e.g., show an error message)
        }
    };


    return (
        <div style={{}}>
            <Paper elevation={0} sx={{
                p: 2
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px' }}>
                    {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {loading ? <CircularProgress /> : <Avatar>{leadDetails?.firstName[0]}</Avatar>}
                        <h3> {loading ? "Loading..." : (leadDetails?.firstName || '') + ' ' + (leadDetails?.lastName || '')}  </h3>
                    </Box> */}
                    <ProfileAvatar pictureUrl={leadDetails?.logoUrl} leadDetails={leadDetails} loading={loading} />
                    <Box sx={{ display: 'flex', gap: 1 }}>

                        <Button variant="contained" onClick={handleOpenDialog}>Convert</Button>
                        <ConfirmationDialog
                            open={openDialog}
                            title="Confirm Action"
                            content="Are you sure you want to convert this lead to a contact?"
                            onClose={handleCloseDialog}
                            onConfirm={handleConfirmAction}
                            confirmText="Confirm"
                            cancelText="Cancel"
                        />

                        <Link to={`/leads/edit/${leadId}`}>
                            <Button variant="outlined">Edit</Button>
                        </Link>
                    </Box>
                </Box>
            </Paper >
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Box id='drawer' sx={{ display: 'flex' }}>
                    <Drawer
                        scrollToSection={scrollToSection}
                        sections={{
                            notesRef,
                            attachmentsRef,
                            openActivitiesRef,
                            closedActivitiesRef,
                            campaignsRef
                        }}
                        relatedEntitiesCount={leadRelatedEntitiesCount} />
                </Box>
                <Box id='main' sx={{ flexGrow: 1, paddingTop: 3, paddingRight: 5 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 3
                        }}
                    >
                        <ToggleButtonGroup
                            color="primary"
                            exclusive
                            aria-label="Platform"
                            value={selectedView}
                            onChange={handleToggle}
                            sx={{ bgcolor: 'white' }}
                        >
                            <ToggleButton value="overview" sx={{ color: 'black' }}>
                                Overview
                            </ToggleButton>
                            <ToggleButton value="history" sx={{ color: 'black' }}>
                                History
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <Typography variant="body2" sx={{ color: 'gray', marginLeft: 2 }}>
                            {latestUpdate}
                        </Typography>
                    </Box>
                    <Box sx={{ '& > * > *': { marginBottom: 3 } }}>
                        {selectedView === "overview" && (
                            <div id='overview'>
                                <div>
                                    <BasicDetails data={leadDetails} />
                                </div>
                                {/* <div>
                                <NextAction />
                            </div> */}
                                <div>
                                    <FullDetails leadData={leadDetails} />
                                </div>
                                <div ref={notesRef}>
                                    <Notes data={leadNotes} fetchNotes={refreshLeadNotes} entity={'leads'} />
                                </div>
                                <div ref={attachmentsRef}>
                                    <Attachments data={leadAttachments} fetchLeadAttachments={refreshLeadAttachments} />
                                </div>
                                <div ref={openActivitiesRef}>
                                    <OpenActivities data={leadTasks} fetchLeadTasks={refreshLeadTasks} fetchClosedTasks={refreshLeadClosedTasks} />
                                </div>
                                <div ref={closedActivitiesRef}>
                                    <ClosedActivities data={leadClosedTasks} fetchClosedTasks={refreshLeadClosedTasks} />
                                </div>
                                <div ref={campaignsRef}>
                                    <Campaigns data={campaigns} fetchCampaigns={refreshCampaigns} leadId={leadId} />
                                </div>

                            </div>
                        )}
                        {selectedView === "history" && (
                            <div id='history'>
                                <EntityTimeline entityId={leadId} entityType={'leads'} />
                            </div>
                        )}
                    </Box>
                </Box>
            </Box>

        </div >
    )
};

export default LeadDetails;